import { useEffect, useRef, useState } from "react";

import { createBanner, destroyBanner, getLastBanner } from "sj-ui-toolkit/src/api/";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { ValidationMessage } from "sj-ui-toolkit/src/components";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { useToast } from "sj-ui-toolkit/src/hooks";

import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Skeleton } from "primereact/skeleton";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BannerData {
  title: string;
  description: string;
}

// ---------------------------------------------------------------------------
// Inline sub-component — single-use skeleton mirroring the "Most recent banner" card
// ---------------------------------------------------------------------------

const CommsSkeleton = () => (
  <Card title={<Skeleton width="30%" height="3rem" />}>
    <div className="flex flex-col gap-4">
      <Skeleton width="80%" height="2rem" />
      <Skeleton width="100%" height="4rem" />
    </div>
  </Card>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const CommsBanner = () => {
  useBreadcrumb({
    label: "Platform Banner",
    route: AdminAppRoutes.routes.comms.routes.banner.route,
    display: "none",
    resetTrail: true,
  });

  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [lastBanner, setLastBanner] = useState<BannerData | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const hasLoadedRef = useRef(false);

  const fetchLast = async () => {
    try {
      const data = await getLastBanner();
      setLastBanner(data ?? null);
    } catch (error) {
      console.error("Failed to fetch banner:", error);
      showToast({ severity: "error", detail: "Failed to load banner" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      fetchLast();
    }
  }, []);

  const onPublish = async () => {
    if (!title || !description) {
      setValidationError("Please provide both a header and message");
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);
    setValidationError(null);

    try {
      await createBanner({ title, description });
      setTitle("");
      setDescription("");
      await fetchLast();
    } catch (error) {
      console.error("Failed to publish banner:", error);
      showToast({ severity: "error", detail: "Failed to publish banner" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);

    try {
      await destroyBanner();
      setLastBanner(null);
    } catch (error) {
      console.error("Failed to delete banner:", error);
      showToast({ severity: "error", detail: "Failed to delete banner" });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex flex-col gap-16">
      <Card title="Add a new banner">
        <div className="flex flex-col gap-4">
          <InputText placeholder="Header" value={title} onChange={e => setTitle(e.target.value)} />
          <InputTextarea
            placeholder="Message"
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
          {validationError && (
            <ValidationMessage severity="warn" text={validationError} role="alert" />
          )}
          <Button
            className="w-fit self-end mt-8"
            label="Publish Banner"
            icon="pi pi-send"
            loading={isSubmitting}
            onClick={onPublish}
          />
        </div>
      </Card>
      {loading ? (
        <CommsSkeleton />
      ) : lastBanner ? (
        <Card title="Most recent banner">
          <div className="flex flex-col gap-4">
            <InputText value={lastBanner.title} readOnly />
            <InputTextarea value={lastBanner.description} readOnly />
            <Button
              className="w-fit self-end mt-8"
              severity="danger"
              label="Delete Banner for All"
              icon="pi pi-times"
              loading={isDeleting}
              onClick={onDelete}
            />
          </div>
        </Card>
      ) : (
        <Card title="No active banners" />
      )}
    </div>
  );
};

export default CommsBanner;
